import { useState } from 'react';

interface SurveillanceRecord {
  id: string;
  location: string;
  targetType: string;
  detectTime: string;
  weather: string;
  visibility: string;
  status: string;
}

export default function NightSurveillance() {
  const [records] = useState<SurveillanceRecord[]>([
    { id: 'N001', location: '港区东侧水域', targetType: '可疑船只', detectTime: '2025-01-15 02:30', weather: '雨雾', visibility: '低', status: '已处理' },
    { id: 'N002', location: '3号码头', targetType: '异常人员', detectTime: '2025-01-15 03:15', weather: '晴', visibility: '中', status: '已处理' },
    { id: 'N003', location: '港池北部', targetType: '漂浮物', detectTime: '2025-01-15 04:00', weather: '雾', visibility: '低', status: '监控中' },
    { id: 'N004', location: '锚地区域', targetType: '落水人员', detectTime: '2025-01-15 01:45', weather: '晴', visibility: '高', status: '已救援' },
  ]);

  const totalNightPatrols = 18;
  const detectedTargets = records.length;
  const rescued = records.filter(r => r.status === '已救援').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">夜间与恶劣天气侦察</h2>
        <div className="flex gap-3">
          <button className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg hover:bg-slate-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-contrast-2-line mr-2"></i>切换热成像
          </button>
          <button className="px-4 py-2 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-flight-takeoff-line mr-2"></i>启动侦察
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">夜间巡逻</span>
            <i className="ri-moon-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{totalNightPatrols}</div>
          <div className="text-xs text-slate-500 mt-1">架次</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">检测目标</span>
            <i className="ri-focus-3-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">{detectedTargets}</div>
          <div className="text-xs text-slate-500 mt-1">个</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">成功救援</span>
            <i className="ri-lifebuoy-line text-teal-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-teal-400">{rescued}</div>
          <div className="text-xs text-slate-500 mt-1">人</div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">当前天气</span>
            <i className="ri-cloudy-line text-slate-400 text-xl"></i>
          </div>
          <div className="text-2xl font-bold text-white">雾</div>
          <div className="text-xs text-slate-500 mt-1">能见度低</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">热成像画面</h3>
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
            <img 
              src="https://public.readdy.ai/ai/img_res/0a3dc3424e70c21fbdb165a63fe05bdc.jpg"
              alt="热成像"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-4 left-4 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
              <i className="ri-contrast-2-line mr-2"></i>
              热成像模式
            </div>
            <div className="absolute top-4 right-4 bg-slate-900/80 backdrop-blur-sm text-white px-3 py-1 rounded-lg text-xs">
              无人机-15
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">检测目标:</span>
                  <span className="text-white ml-2">3个</span>
                </div>
                <div>
                  <span className="text-slate-400">环境温度:</span>
                  <span className="text-white ml-2">8°C</span>
                </div>
                <div>
                  <span className="text-slate-400">能见度:</span>
                  <span className="text-orange-400 ml-2">低</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">夜视画面</h3>
          <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
            <img 
              src="https://readdy.ai/api/search-image?query=Night%20vision%20green%20tinted%20view%20from%20a%20surveillance%20drone%20showing%20a%20port%20area%20with%20ships%20and%20containers%20in%20low%20light%20conditions%20with%20enhanced%20visibility%20professional%20night%20vision%20technology%20military%20grade%20surveillance%20equipment%20realistic%20security%20monitoring%20style&width=600&height=400&seq=night-vision-001&orientation=landscape"
              alt="夜视"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium flex items-center">
              <i className="ri-eye-line mr-2"></i>
              夜视模式
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-slate-900/80 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white font-medium">港区东侧水域</span>
                <span className="text-green-400">清晰度: 高</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">侦察记录</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">记录编号</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">位置</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">目标类型</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">检测时间</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">天气</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">能见度</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">状态</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{record.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{record.location}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{record.targetType}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{record.detectTime}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{record.weather}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      record.visibility === '高' ? 'bg-teal-500/20 text-teal-400' :
                      record.visibility === '中' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}>
                      {record.visibility}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      record.status === '已救援' ? 'bg-teal-500/20 text-teal-400' :
                      record.status === '已处理' ? 'bg-slate-500/20 text-slate-400' :
                      'bg-orange-500/20 text-orange-400'
                    }`}>
                      {record.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button className="text-teal-400 hover:text-teal-300 mr-3 cursor-pointer">
                      <i className="ri-video-line"></i>
                    </button>
                    <button className="text-slate-400 hover:text-slate-300 cursor-pointer">
                      <i className="ri-file-text-line"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
